import type React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { templates } from '@xing-com/crate-lebenslauf-constants';
import { useBasePath } from '@xing-com/crate-lebenslauf-hooks';
import { IconHome } from '@xing-com/icons';
import { SeoBreadcrumb } from '@xing-com/seo-breadcrumb';
import { Hero, Headline, BodyCopy } from '@xing-com/typography';

import {
  StyledSection,
  StyledIllustrationWrapper,
  StyledHeadline,
  StyledIllustration,
  StyledTemplateWrapper,
  StyledTemplateImage,
  StyledButton,
  StyledBodyCopy,
} from './templates.styled';

export const Templates: React.FC = () => {
  const { formatMessage } = useIntl();
  const basePath = useBasePath();

  return (
    <>
      <SeoBreadcrumb
        size="large"
        items={[
          {
            title: formatMessage({
              id: 'CRATE_LEBENSLAUF_NAVIGATION_HOME',
            }),
            url: basePath,
            icon: IconHome,
          },
          {
            title: formatMessage({
              id: 'CRATE_LEBENSLAUF_NAVIGATION_TEMPLATES',
            }),
            url: `${basePath}/vorlagen-und-muster`,
          },
        ]}
      />
      <StyledSection>
        <Hero>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_HERO" />
        </Hero>
        <StyledIllustrationWrapper>
          <div>
            <BodyCopy>
              <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_INTRO_1" />
            </BodyCopy>
            <BodyCopy>
              <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_INTRO_2" />
            </BodyCopy>
            <BodyCopy>
              <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_INTRO_3" />
            </BodyCopy>
          </div>
          <StyledIllustration width="415" height="307" />
        </StyledIllustrationWrapper>
      </StyledSection>

      <StyledSection>
        <StyledHeadline size="xxxlarge">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_HEADLINE" />
        </StyledHeadline>
        {templates
          // we only show the first 5 templates because SEO reasons
          .slice(0, 5)
          .map(
            ({ name, title, description, imageUrl, bgColor, createWith }) => (
              <StyledTemplateWrapper key={name} $color={bgColor}>
                <StyledTemplateImage
                  width="261"
                  height="369"
                  src={imageUrl}
                  alt=""
                />
                <div>
                  <Headline size="xxlarge">
                    <FormattedMessage id={title} />
                  </Headline>
                  <BodyCopy>
                    <FormattedMessage id={description} />
                  </BodyCopy>
                  <StyledButton
                    variant="secondary"
                    to={`${basePath}/neu#${createWith.template}-${createWith.color}`.toLowerCase()}
                  >
                    <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_CTA" />
                  </StyledButton>
                </div>
              </StyledTemplateWrapper>
            )
          )}
      </StyledSection>

      <StyledSection>
        <StyledHeadline size="xxxlarge">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_HEADLINE" />
        </StyledHeadline>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q1" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A1" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q2" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A2" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q3" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A3" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q4" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A4" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q5" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A5" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q6" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A6" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q7" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A7" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q9" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A9" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q10" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A10" />
        </StyledBodyCopy>
        <Headline size="large">
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_Q11" />
        </Headline>
        <StyledBodyCopy>
          <FormattedMessage id="CRATE_LEBENSLAUF_TEMPLATES_PAGE_FAQ_A11" />
        </StyledBodyCopy>
      </StyledSection>
    </>
  );
};
